import { a as index$1 } from './index-C7a5WG_C.js';
import { E as Environment, a as addKeysToHeadersOverride } from './index-CEinhwa2.js';
import globalAxios from 'axios';

/**
 * Custom Error class that is returned from the API when a request fails
 */
class APIError extends Error {
    code;
    details;
    link;
    traceId;
    constructor({ message, code, details, link, trace_id: traceId, }) {
        super(message);
        this.code = code;
        this.details = details;
        this.link = link;
        this.traceId = traceId;
    }
}

/* eslint-disable implicit-arrow-linebreak */
const defaultHeaders = {
    sdkVersion: 'ts-immutable-sdk-multi-rollup-api-client-1.78.10',
};
/**
 * createAPIConfiguration to create a custom Configuration
 * other than the production and sandbox defined below.
 */
const createAPIConfiguration = (overrides) => {
    const { baseConfig, basePath, headers: baseHeaders, } = overrides;
    if (!basePath.trim()) {
        throw Error('basePath can not be empty');
    }
    const headers = {
        ...(baseHeaders || {}),
        ...(addKeysToHeadersOverride(baseConfig, overrides) || {}),
        ...defaultHeaders
    };
    const configParams = {
        ...baseConfig,
        basePath,
        baseOptions: { headers },
    };
    return new index$1.Configuration(configParams);
};
class BlockchainDataConfiguration {
    apiConfig;
    baseConfig;
    constructor({ baseConfig, overrides }) {
        this.baseConfig = baseConfig;
        if (overrides) {
            this.apiConfig = createAPIConfiguration(overrides);
        }
        else {
            switch (baseConfig.environment) {
                case Environment.SANDBOX: {
                    this.apiConfig = createAPIConfiguration({
                        basePath: 'https://api.sandbox.immutable.com',
                        baseConfig,
                    });
                    break;
                }
                case Environment.PRODUCTION: {
                    this.apiConfig = createAPIConfiguration({
                        basePath: 'https://api.immutable.com',
                        baseConfig,
                    });
                    break;
                }
                default: {
                    this.apiConfig = createAPIConfiguration({
                        basePath: 'https://api.sandbox.immutable.com',
                        baseConfig,
                    });
                }
            }
        }
    }
}

/* eslint-disable @typescript-eslint/naming-convention */
/**
 * [Formats an error in the APIError shape](https://axios-http.com/docs/handling_errors)
 * @param error - The Error object thrown by the request
 * @returns APIError
 */
function formatError(error) {
    if (globalAxios.isAxiosError(error) && error.response) {
        const apiError = error.response.data;
        if (apiError.code && apiError.message) {
            return new APIError({
                code: apiError.code,
                message: apiError.message,
                details: apiError.details || null,
                link: apiError.link || '',
                trace_id: apiError.trace_id || '',
            });
        }
        return new APIError({
            code: error.code ?? error.response?.status.toString() ?? 'unknown_error_code',
            message: String(error),
            details: null,
            link: '',
            trace_id: '',
        });
    }
    return new APIError({
        code: 'unknown_error_code',
        message: String(error),
        details: null,
        link: '',
        trace_id: '',
    });
}

class BlockchainData {
    config;
    activities;
    chains;
    collections;
    nfts;
    nftOwners;
    tokens;
    metadata;
    crafting;
    pricing;
    metadataSearch;
    constructor(moduleConfig) {
        this.config = new BlockchainDataConfiguration(moduleConfig);
        this.activities = new index$1.ActivitiesApi(this.config.apiConfig);
        this.chains = new index$1.ChainsApi(this.config.apiConfig);
        this.collections = new index$1.CollectionsApi(this.config.apiConfig);
        this.nfts = new index$1.NftsApi(this.config.apiConfig);
        this.nftOwners = new index$1.NftOwnersApi(this.config.apiConfig);
        this.tokens = new index$1.TokensApi(this.config.apiConfig);
        this.metadata = new index$1.MetadataApi(this.config.apiConfig);
        this.crafting = new index$1.CraftingApi(this.config.apiConfig);
        this.pricing = new index$1.PricingApi(this.config.apiConfig);
        this.metadataSearch = new index$1.MetadataSearchApi(this.config.apiConfig);
    }
    /**
     * List all activities
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of activities
     * @throws {@link index.APIError}
     */
    async listActivities(request) {
        return (await this.activities
            .listActivities(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * List activities sorted by updated_at timestamp ascending, useful for time based data replication
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of activities
     * @throws {@link index.APIError}
     */
    async listActivityHistory(request) {
        return (await this.activities
            .listActivityHistory(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Get a single activity by ID
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a single activity
     * @throws {@link index.APIError}
     */
    async getActivity(request) {
        return (await this.activities
            .getActivity(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * List supported chains
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of supported chains
     * @throws {@link index.APIError}
     */
    async listChains(request) {
        return (await this.chains
            .listChains(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * List all collections
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of collections
     * @throws {@link index.APIError}
     */
    async listCollections(request) {
        return (await this.collections
            .listCollections(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * List collections by NFT owner
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of collections
     * @throws {@link index.APIError}
     */
    async listCollectionsByNFTOwner(request) {
        return (await this.collections
            .listCollectionsByNFTOwner(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Get a collection by contract address
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a single collection
     * @throws {@link index.APIError}
     */
    async getCollection(request) {
        return (await this.collections
            .getCollection(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Get NFT by token ID
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a single NFT
     * @throws {@link index.APIError}
     */
    async getNFT(request) {
        return (await this.nfts
            .getNFT(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * List NFTs by contract address
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of NFTs
     * @throws {@link index.APIError}
     */
    async listNFTs(request) {
        return (await this.nfts
            .listNFTs(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * List NFTs by account address
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of NFTs
     * @throws {@link index.APIError}
     */
    async listNFTsByAccountAddress(request) {
        return (await this.nfts
            .listNFTsByAccountAddress(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * List All NFTs on a chain
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of NFTs
     * @throws {@link index.APIError}
     */
    async listAllNFTs(request) {
        return (await this.nfts
            .listAllNFTs(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Create a mint request to mint a set of NFTs for a given collection
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with the remaining rate limits
     * @throws {@link index.APIError}
     */
    async createMintRequest(request) {
        return (await this.nfts
            .createMintRequest(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * List all mint requests for a given contract address
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of mint requests
     * @throws {@link index.APIError}
     */
    async listMintRequests(request) {
        return (await this.nfts
            .listMintRequests(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Retrieve the status of a single mint request identified by its reference ID
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a single mint request
     * @throws {@link index.APIError}
     */
    async getMintRequest(request) {
        return (await this.nfts
            .getMintRequest(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * List NFT owners by token ID
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of NFT owners
     * @throws {@link index.APIError}
     */
    async listNFTOwners(request) {
        return (await this.nftOwners
            .listNFTOwners(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * List NFT owners by contract address
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of NFT owners
     * @throws {@link index.APIError}
     */
    async listNFTOwnersByContractAddress(request) {
        return (await this.nftOwners
            .listOwnersByContractAddress(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * List All NFT owners on a chain
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of NFT owners
     * @throws {@link index.APIError}
     */
    async listAllNFTOwners(request) {
        return (await this.nftOwners
            .listAllNFTOwners(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * List ERC20 Token contracts
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of ERC20 Tokens
     * @throws {@link index.APIError}
     */
    async listTokens(request) {
        return (await this.tokens
            .listERC20Tokens(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Get details for an ERC20 Token by contract address
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of ERC20 Tokens
     * @throws {@link index.APIError}
     */
    async getToken(request) {
        return (await this.tokens
            .getERC20Token(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Get metadata by ID
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a single Metadata
     * @throws {@link index.APIError}
     */
    async getMetadata(request) {
        return (await this.metadata
            .getMetadata(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * List NFT Metadata by contract address
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of Metadata
     * @throws {@link index.APIError}
     */
    async listNFTMetadataByContractAddress(request) {
        return (await this.metadata
            .listMetadata(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * List NFT Metadata by chain
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of Metadata
     * @throws {@link index.APIError}
     */
    async listNFTMetadataByChain(request) {
        return (await this.metadata
            .listMetadataForChain(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Refresh collection metadata
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with the updated collection
     * @throws {@link index.APIError}
     */
    async refreshCollectionMetadata(request) {
        return (await this.collections
            .refreshCollectionMetadata(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Refresh metadata for specific NFTs
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with the remaining rate limits
     * @throws {@link index.APIError}
     */
    async refreshNFTMetadata(request) {
        return (await this.metadata
            .refreshNFTMetadataByTokenID(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Refresh metadata by ID. This will refresh metadata for all NFTs that reference the given metadata ID.
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with the remaining rate limits
     * @throws {@link index.APIError}
     */
    async refreshStackedMetadata(request) {
        return (await this.metadata
            .refreshMetadataByID(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Sign a crafting payload
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with the signature result
     * @throws {@link index.APIError}
     */
    async signCraftingPayload(request) {
        return (await this.crafting
            .signCraftingPayload(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Marketplace APIs
     * - Metadata search
     */
    /**
     * Get list of metadata filters
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with the a list of metadata filters
     * @throws {@link index.APIError}
     */
    async listFilters(request) {
        return (await this.metadataSearch
            .listFilters(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Search NFTs
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with the a list NFTs with metadata, market and listings information
     * @throws {@link index.APIError}
     */
    async searchNFTs(request) {
        return (await this.metadataSearch
            .searchNFTs(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Search NFT stacks
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with the a list NFT stacks with stack count, market and listings information
     * @throws {@link index.APIError}
     */
    async searchStacks(request) {
        return (await this.metadataSearch
            .searchStacks(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Marketplace APIs
     * - Pricing
     */
    /**
     * Get pricing data for a list of token ids
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with the a list of pricing data for particular NFTs
     * @throws {@link index.APIError}
     */
    async quotesForNFTs(request) {
        return (await this.pricing
            .quotesForNFTs(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Get pricing data for a list of stack ids
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with the a list of pricing data for particular stack ids
     * @throws {@link index.APIError}
     */
    async quotesForStacks(request) {
        return (await this.pricing
            .quotesForStacks(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
    /**
     * Marketplace APIs
     * - Metadata
     */
    /**
     * List NFT stack bundles by stack_id.
     * @param request - the request object containing the parameters to be provided in the API request
     * @returns a promise that resolves with a list of NFT stacks with stack count, market and listings information
     * @throws {@link index.APIError}
     */
    async listStacks(request) {
        return (await this.metadata
            .listStacks(request)
            .then((res) => res.data)
            .catch((err) => {
            throw formatError(err);
        }));
    }
}

export { APIError as A, BlockchainData as B };
